"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useReducer,
  useRef,
  useState,
} from "react";

import type { SearchField } from "@/lib/analytics/analytics-events";
import { track } from "@/lib/analytics/data-layer";
import { searchPublicGiftLists } from "@/lib/gift-list-search/public-search-client";
import { publicSearchRequestSchema } from "@/lib/gift-list-search/public-search-schemas";
import type {
  PublicSearchPage,
  SearchCriteria,
} from "@/lib/gift-list-search/public-search-types";
import {
  SearchConcurrencyController,
  type SearchOperationToken,
} from "@/lib/gift-list-search/search-concurrency";
import {
  LoadMoreSingleFlight,
  getNextPage,
  type LoadMoreFlightToken,
} from "@/lib/gift-list-search/search-pagination";
import {
  canStartPreview,
  initialPublicSearchState,
  publicSearchReducer,
  type PublicSearchAction,
  type PublicSearchState,
} from "@/lib/gift-list-search/search-state";

export interface PublicGiftListSearchController {
  state: PublicSearchState;
  resetVersion: number;
  initialSearchVersion: number;
  submit(input: SearchCriteria): void;
  loadMore(): void;
  retry(): void;
  expandSearch(): void;
  clear(): void;
}

/** Fetches one page of results; same contract as the public search client. */
export type PublicSearchFn = typeof searchPublicGiftLists;

export interface PublicGiftListSearchOptions {
  /**
   * Fetches each page (first page, load-more and the name-only preview). Defaults to the
   * public BFF client. Failures must reject, as the public client does.
   */
  search?: PublicSearchFn;
}

/**
 * The search the person confirmed, and what it found. Only which fields were filled leaves
 * (`fields_used`): the name, the date and the place they typed are exactly what must not.
 */
function reportSearch(criteria: SearchCriteria, resultsCount: number): void {
  const fieldsUsed: SearchField[] = [
    ...(criteria.query === undefined ? [] : (["name"] as const)),
    ...(criteria.eventDateFrom === undefined ? [] : (["date"] as const)),
    ...(criteria.eventLocation === undefined ? [] : (["location"] as const)),
  ];

  track({
    event: "search",
    search_scope: "list",
    fields_used: fieldsUsed,
    results_count: resultsCount,
    zero_results: resultsCount === 0,
  });
  if (resultsCount === 0) track({ event: "search_zero_results", search_scope: "list", fields_used: fieldsUsed });
}

type EmptySearchState = Extract<PublicSearchState, { phase: "empty" }>;

/**
 * Drives the gift list search: reducer state, request concurrency and single-flight
 * load-more. The fetcher is injectable so other surfaces reuse the same flow.
 */
export function usePublicGiftListSearch({
  search = searchPublicGiftLists,
}: PublicGiftListSearchOptions = {}): PublicGiftListSearchController {
  const [state, reactDispatch] = useReducer(
    publicSearchReducer,
    initialPublicSearchState,
  );
  const [resetVersion, setResetVersion] = useState(0);
  const [initialSearchVersion, setInitialSearchVersion] = useState(0);
  const stateRef = useRef<PublicSearchState>(initialPublicSearchState);
  // Read through a ref so every controller callback keeps its identity even when the
  // caller passes a new function on each render.
  const searchRef = useRef<PublicSearchFn>(search);
  const concurrencyRef = useRef<SearchConcurrencyController | null>(null);
  const loadMoreFlightRef = useRef<LoadMoreSingleFlight | null>(null);
  const lifecycleCleanedRef = useRef(false);

  if (concurrencyRef.current === null) {
    concurrencyRef.current = new SearchConcurrencyController();
  }
  if (loadMoreFlightRef.current === null) {
    loadMoreFlightRef.current = new LoadMoreSingleFlight();
  }

  useLayoutEffect(() => {
    // Layout phase: the latest fetcher is in place before any passive effect or user
    // event can start a request.
    searchRef.current = search;
  }, [search]);

  const dispatch = useCallback((action: PublicSearchAction) => {
    stateRef.current = publicSearchReducer(stateRef.current, action);
    reactDispatch(action);
  }, []);

  const startPreview = useCallback(
    (emptyState: EmptySearchState) => {
      if (!canStartPreview(emptyState)) return;
      const query = emptyState.criteria.query;
      if (query === undefined) return;

      const concurrency = concurrencyRef.current;
      if (concurrency === null) return;
      const token = concurrency.startPreview();
      dispatch({ type: "PREVIEW_STARTED" });

      void searchRef.current({ query, page: 1 }, token.signal).then(
        (page) => {
          if (concurrency.finish(token)) {
            dispatch({ type: "PREVIEW_SUCCEEDED", page });
          }
        },
        () => {
          if (concurrency.finish(token)) {
            dispatch({ type: "PREVIEW_FAILED" });
          }
        },
      );
    },
    [dispatch],
  );

  const finishInitial = useCallback(
    (
      criteria: SearchCriteria,
      concurrency: SearchConcurrencyController,
      token: SearchOperationToken,
      page: PublicSearchPage,
    ) => {
      if (!concurrency.finish(token)) return;
      reportSearch(criteria, page.totalItems);
      dispatch({ type: "INITIAL_SUCCEEDED", page });
      const nextState = stateRef.current;
      if (nextState.phase === "empty") startPreview(nextState);
    },
    [dispatch, startPreview],
  );

  const runInitialRequest = useCallback(
    (
      criteria: SearchCriteria,
      concurrency: SearchConcurrencyController,
      token: SearchOperationToken,
    ) => {
      void searchRef.current({ ...criteria, page: 1 }, token.signal).then(
        (page) => finishInitial(criteria, concurrency, token, page),
        () => {
          if (concurrency.finish(token)) dispatch({ type: "INITIAL_FAILED" });
        },
      );
    },
    [dispatch, finishInitial],
  );

  const runLoadMoreRequest = useCallback(
    (
      criteria: SearchCriteria,
      pageNumber: number,
      concurrency: SearchConcurrencyController,
      operationToken: SearchOperationToken,
      singleFlight: LoadMoreSingleFlight,
      flightToken: LoadMoreFlightToken,
    ) => {
      const finish = (action: PublicSearchAction) => {
        const operationIsCurrent = concurrency.finish(operationToken);
        const flightIsCurrent = singleFlight.finish(flightToken);
        if (operationIsCurrent && flightIsCurrent) dispatch(action);
      };

      void searchRef.current(
        { ...criteria, page: pageNumber },
        operationToken.signal,
      ).then(
        (page) => finish({ type: "LOAD_MORE_SUCCEEDED", page }),
        () => finish({ type: "LOAD_MORE_FAILED" }),
      );
    },
    [dispatch],
  );

  const submit = useCallback(
    (input: SearchCriteria) => {
      const parsed = publicSearchRequestSchema.safeParse({ ...input, page: 1 });
      if (!parsed.success) return;

      const criteria: SearchCriteria = {
        ...(parsed.data.query === undefined ? {} : { query: parsed.data.query }),
        ...(parsed.data.eventDateFrom === undefined
          ? {}
          : { eventDateFrom: parsed.data.eventDateFrom }),
        ...(parsed.data.eventLocation === undefined
          ? {}
          : { eventLocation: parsed.data.eventLocation }),
      };
      const concurrency = concurrencyRef.current;
      const singleFlight = loadMoreFlightRef.current;
      if (concurrency === null || singleFlight === null) return;

      singleFlight.reset();
      const token = concurrency.startInitial();
      setInitialSearchVersion((version) => version + 1);
      dispatch({ type: "SUBMIT", criteria });
      runInitialRequest(criteria, concurrency, token);
    },
    [dispatch, runInitialRequest],
  );

  const loadMore = useCallback(() => {
    const currentState = stateRef.current;
    if (
      currentState.phase !== "results" ||
      currentState.failedOperation !== null
    ) {
      return;
    }

    const pageNumber = getNextPage(currentState.metadata);
    const concurrency = concurrencyRef.current;
    const singleFlight = loadMoreFlightRef.current;
    if (pageNumber === null || concurrency === null || singleFlight === null) {
      return;
    }

    const flightToken = singleFlight.tryStart(pageNumber);
    if (flightToken === null) return;
    const operationToken = concurrency.startLoadMore();
    dispatch({ type: "LOAD_MORE_STARTED", page: pageNumber });
    runLoadMoreRequest(
      currentState.criteria,
      pageNumber,
      concurrency,
      operationToken,
      singleFlight,
      flightToken,
    );
  }, [dispatch, runLoadMoreRequest]);

  const retry = useCallback(() => {
    const currentState = stateRef.current;
    const concurrency = concurrencyRef.current;
    const singleFlight = loadMoreFlightRef.current;
    if (concurrency === null || singleFlight === null) return;

    if (currentState.phase === "error") {
      const { criteria } = currentState.failedOperation;
      singleFlight.reset();
      const token = concurrency.startInitial();
      setInitialSearchVersion((version) => version + 1);
      dispatch({ type: "RETRY" });
      runInitialRequest(criteria, concurrency, token);
      return;
    }

    if (
      currentState.phase !== "results" ||
      currentState.failedOperation === null
    ) {
      return;
    }

    const { criteria, page } = currentState.failedOperation;
    const flightToken = singleFlight.tryStart(page);
    if (flightToken === null) return;
    const operationToken = concurrency.startLoadMore();
    dispatch({ type: "RETRY" });
    runLoadMoreRequest(
      criteria,
      page,
      concurrency,
      operationToken,
      singleFlight,
      flightToken,
    );
  }, [dispatch, runInitialRequest, runLoadMoreRequest]);

  const expandSearch = useCallback(() => {
    dispatch({ type: "PREVIEW_ADOPTED" });
    // Adopting drops the event date, so the form is remounted to clear the field
    // as well. The typed name survives: the page reseeds it from the criteria.
    setResetVersion((version) => version + 1);
  }, [dispatch]);

  const clear = useCallback(() => {
    concurrencyRef.current?.dispose();
    loadMoreFlightRef.current?.reset();
    concurrencyRef.current = new SearchConcurrencyController();
    loadMoreFlightRef.current = new LoadMoreSingleFlight();
    dispatch({ type: "CLEAR" });
    setResetVersion((version) => version + 1);
  }, [dispatch]);

  useEffect(() => {
    // React Strict Mode repeats effect setup/cleanup in development. Recreate
    // disposed controllers for that second setup while keeping one stable pair
    // throughout each real mounted lifecycle.
    if (lifecycleCleanedRef.current) {
      concurrencyRef.current = new SearchConcurrencyController();
      loadMoreFlightRef.current = new LoadMoreSingleFlight();
      lifecycleCleanedRef.current = false;
    }

    return () => {
      lifecycleCleanedRef.current = true;
      concurrencyRef.current?.dispose();
      loadMoreFlightRef.current?.reset();
    };
  }, []);

  return {
    state,
    resetVersion,
    initialSearchVersion,
    submit,
    loadMore,
    retry,
    expandSearch,
    clear,
  };
}
